/**
 * The browser half of the room relay.
 *
 * One socket, four callbacks, and a send buffer for the window between
 * `new WebSocket(...)` and its `open` event — without which the host's very
 * first ICE candidate (which can be produced synchronously from a host
 * candidate) is silently dropped.
 */

import { browserSocket } from "./browser-rtc.ts";
import type { SocketFactory } from "./peer-connection.ts";
import {
  decodeSignal,
  decodeRtcSignal,
  encodeRtcSignal,
  encodeSignal,
  type RtcSignal,
  type SignalRole,
} from "./signaling.ts";

export interface SignalingHooks {
  /** Which seat the relay gave this socket. Fires once, first. */
  readonly onRole: (role: SignalRole) => void;
  /** The other seat filled (`true`) or emptied (`false`). */
  readonly onPeer: (present: boolean) => void;
  readonly onSignal: (signal: RtcSignal) => void;
  /** The relay went away, or turned this socket away because the room was full. */
  readonly onClosed: (reason: string) => void;
}

export interface SignalingClient {
  send(signal: RtcSignal): void;
  close(): void;
}

export function createSignalingClient(
  url: string,
  hooks: SignalingHooks,
  factory: SocketFactory = browserSocket,
): SignalingClient {
  const socket = factory(url);
  const backlog: string[] = [];
  let open = false;
  let closedReason: string | null = null;

  function flush(frame: string): void {
    if (open) socket.send(frame);
    else backlog.push(frame);
  }

  function finish(reason: string): void {
    if (closedReason !== null) return;
    closedReason = reason;
    hooks.onClosed(reason);
  }

  socket.onopen = () => {
    open = true;
    for (const frame of backlog.splice(0)) socket.send(frame);
  };
  socket.onerror = (message) => finish(message);
  socket.onclose = () => finish("relay closed");
  socket.onmessage = (frame) => {
    const message = decodeSignal(frame);
    if (!message) return;
    if (message.t === "joined") return hooks.onRole(message.role);
    if (message.t === "peer") return hooks.onPeer(message.present);
    if (message.t === "full") return finish("that room already has two players");
    const signal = decodeRtcSignal(message.body);
    if (signal) hooks.onSignal(signal);
  };

  return {
    send: (signal) => flush(encodeSignal({ t: "relay", body: encodeRtcSignal(signal) })),
    close: () => {
      open = false;
      socket.close();
    },
  };
}
